/**
 * Tile vocabulary. Levels are authored as rows of ASCII characters (see LEGEND) which
 * are translated into these numeric ids at load time.
 */

export const T = {
  EMPTY: 0,
  /** Terrain: dirt/rock mass with a decorated top edge. */
  GROUND: 1,
  /** Hard solid block used for stairs, ledges and pipe pedestals. */
  ROCK: 2,
  /** Breakable brick (bumps when small, shatters when powered up). */
  BRICK: 3,
  /** Brick holding a stash of coins. */
  BRICK_COINS: 4,
  /** Brick holding a power-up. */
  BRICK_ITEM: 5,
  /** Bonus block that gives a coin. */
  QUESTION: 6,
  /** Bonus block that gives a power-up (kind depends on player form). */
  QUESTION_ITEM: 7,
  /** Bonus block that gives an extra life. */
  QUESTION_LIFE: 8,
  /** Invisible block containing a coin. */
  HIDDEN_COIN: 9,
  /** Invisible block containing an extra life. */
  HIDDEN_LIFE: 10,
  /** Spent block. */
  USED: 11,

  PIPE_TL: 12,
  PIPE_TR: 13,
  PIPE_BL: 14,
  PIPE_BR: 15,
  /** Horizontal pipe mouth (opening faces left). */
  PIPE_MOUTH_T: 16,
  PIPE_MOUTH_B: 17,
  PIPE_SHAFT_T: 18,
  PIPE_SHAFT_B: 19,

  /** One-way platform: you can jump up through it and land on top. */
  PLANK: 20,

  /** Goal pole (not solid) and its cap. */
  FLAGPOLE: 21,
  FLAG_TOP: 22,
  /** Castle masonry (solid). */
  CASTLE: 23,
  /** Solid block themed as cave crystal, purely cosmetic difference. */
  CRYSTAL: 24,
} as const;

export type TileId = (typeof T)[keyof typeof T];

/** Characters used in level row data. */
export const LEGEND: Record<string, number> = {
  '.': T.EMPTY,
  ' ': T.EMPTY,
  '#': T.GROUND,
  R: T.ROCK,
  B: T.BRICK,
  C: T.BRICK_COINS,
  I: T.BRICK_ITEM,
  '?': T.QUESTION,
  Q: T.QUESTION_ITEM,
  '1': T.QUESTION_LIFE,
  H: T.HIDDEN_COIN,
  h: T.HIDDEN_LIFE,
  U: T.USED,
  '[': T.PIPE_TL,
  ']': T.PIPE_TR,
  '{': T.PIPE_BL,
  '}': T.PIPE_BR,
  J: T.PIPE_MOUTH_T,
  j: T.PIPE_MOUTH_B,
  N: T.PIPE_SHAFT_T,
  n: T.PIPE_SHAFT_B,
  '=': T.PLANK,
  '|': T.FLAGPOLE,
  '^': T.FLAG_TOP,
  W: T.CASTLE,
  X: T.CRYSTAL,
};

/** Characters that describe an entity rather than a tile. Tile becomes EMPTY. */
export const ENTITY_LEGEND: Record<string, string> = {
  o: 'coin',
  g: 'grub',
  k: 'snug',
  v: 'nipper',
  S: 'spawn',
  p: 'checkpoint',
};

const SOLID = new Set<number>([
  T.GROUND,
  T.ROCK,
  T.BRICK,
  T.BRICK_COINS,
  T.BRICK_ITEM,
  T.QUESTION,
  T.QUESTION_ITEM,
  T.QUESTION_LIFE,
  T.HIDDEN_COIN,
  T.HIDDEN_LIFE,
  T.USED,
  T.PIPE_TL,
  T.PIPE_TR,
  T.PIPE_BL,
  T.PIPE_BR,
  T.PIPE_MOUTH_T,
  T.PIPE_MOUTH_B,
  T.PIPE_SHAFT_T,
  T.PIPE_SHAFT_B,
  T.CASTLE,
  T.CRYSTAL,
]);

/** Blocks that pop upward when hit from below. */
const BUMPABLE = new Set<number>([
  T.BRICK,
  T.BRICK_COINS,
  T.BRICK_ITEM,
  T.QUESTION,
  T.QUESTION_ITEM,
  T.QUESTION_LIFE,
  T.HIDDEN_COIN,
  T.HIDDEN_LIFE,
]);

/** Bricks a powered-up player can shatter. */
const BREAKABLE = new Set<number>([T.BRICK]);

const PIPE = new Set<number>([
  T.PIPE_TL,
  T.PIPE_TR,
  T.PIPE_BL,
  T.PIPE_BR,
  T.PIPE_MOUTH_T,
  T.PIPE_MOUTH_B,
  T.PIPE_SHAFT_T,
  T.PIPE_SHAFT_B,
]);

/** Blocks drawn as invisible until they are hit. */
const HIDDEN = new Set<number>([T.HIDDEN_COIN, T.HIDDEN_LIFE]);

export function isSolidTile(t: number): boolean {
  return SOLID.has(t);
}

export function isOneWayTile(t: number): boolean {
  return t === T.PLANK;
}

export function isBumpableTile(t: number): boolean {
  return BUMPABLE.has(t);
}

export function isBreakableTile(t: number): boolean {
  return BREAKABLE.has(t);
}

export function isPipeTile(t: number): boolean {
  return PIPE.has(t);
}

export function isHiddenTile(t: number): boolean {
  return HIDDEN.has(t);
}

export function charToTile(ch: string): number {
  const t = LEGEND[ch];
  return t === undefined ? T.EMPTY : t;
}
